<template>
  <q-card
    :dark="isDarkModeGlobal"
    :flat="time_parameters_valid"
  >
    <q-card-actions>
      <q-space />
      <q-btn
        icon="download"
        color="primary"
        round
        @click="downloadCanvasAsSVG('my-timing-graph.svg')"
      >
        <q-tooltip>
          Download Graph as SVG
        </q-tooltip>
      </q-btn>
    </q-card-actions>

    <q-card-section v-if="!time_parameters_valid">
      <q-banner
        v-if="!time_parameters_valid"
        class="text-white bg-red"
      >
        {{ message }}
      </q-banner>
    </q-card-section>
    <q-card-section>
      <q-btn
        class="content-center"
        size="md"
        rounded
        v-if="canvasRefreshButton"
        icon="graphic_eq"
        color="secondary"
        @click="draw"
        :loading="safariLoading"
        label="Generate graph"
      >
        <q-tooltip>
          Click button to generate the timing plot using new timing parameters
        </q-tooltip>
      </q-btn>
    </q-card-section>
    <q-card-section>
      <canvas
        id="canvasRef"
        ref="canvasRef"
        :width="canvasWidth"
        :height="canvasHeight"
      />
    </q-card-section>
  </q-card>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch, onBeforeUnmount} from 'vue'
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {debounce} from "quasar"
import Worker from '../workers/offscreencanvas?worker'
import C2S from 'canvas2svg'
import { WorkerState } from '../workers/common.js'
import { drawTimingGraphs } from '../workers/drawing.js'

const canvasWidth = computed(() => props.parentWidth)
const canvasHeight = computed(() => 800)

const calculatorStore = useCalculatorStore()
const {
  pulse_duration_ms,
  pulse_ramp_duration_ms,
  pulse_repetition_interval_ms,
  pulse_train_duration_ms,
  pulse_train_ramp_duration_ms,
  pulse_train_ramp_shape,
  pulse_train_repetition_ramp_shape,
  pulse_train_repetition_ramp_duration_ms,
  pulse_train_repetition_interval_ms,
  pulse_train_repetition_duration_ms,
  frequency_MHz,
  ppp_mpa,
  derated_ppp_mpa,
  pulse_ramp_shape,
  time_parameters_valid,
  isSecondLevelTimingParameters,
  isThirdLevelTimingParameters,
  pulse_train_repetition_duration_max,
  isDarkModeGlobal,
  isppa,
  canvasRefreshButton
} = storeToRefs(calculatorStore)

const props = defineProps(['parentWidth'])
const emit = defineEmits(['loading', 'updateSize'])
const message = "Unfortunately these timing parameters will not work. Please change the value or reset all your parameters."
const pulseRepetitionInterval = computed(() => Number(pulse_repetition_interval_ms.value))
const pulseTrainDuration = computed(() => Number(pulse_train_duration_ms.value))
const pulseTrainRampDuration = computed(() => Number(pulse_train_ramp_duration_ms.value))
const pulseTrainRepetitionInterval = computed(() => Number(pulse_train_repetition_interval_ms.value))
const pulseTrainRepetitionDuration = computed(() => Number(pulse_train_repetition_duration_ms.value))
const pulseTrainRepetitionRampDuration = computed(() => Number(pulse_train_repetition_ramp_duration_ms.value))
const arbitrary_default_amplitude = 100
const amplitude = computed(() => ppp_mpa.value * arbitrary_default_amplitude)
const frequency = computed(() => Number(frequency_MHz.value))
const sampleRate = computed(() => 250 / frequency.value)
// const sampleRate = computed(() => frequency.value * 50000)
const nPulses = computed(() =>   pulseTrainDuration.value / pulseRepetitionInterval.value)
const nTrains = computed(() =>   pulse_train_repetition_duration_ms.value / pulseTrainRepetitionInterval.value)
const ptrd_isValid = computed(() => +pulse_train_repetition_duration_ms.value <= pulse_train_repetition_duration_max.value)

let worker = null
const loading = ref(false)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const safariLoading = ref(false)

const downloadCanvasAsSVG = (filename: string = 'timing-graph.svg'): void => {
  try {
    const width = canvasWidth.value as number
    const height = canvasHeight.value as number

    // Build the same data object that the worker receives
    const data = {
      z_Rayl_IS: calculatorStore.z_Rayl_IS,
      z_Rayl_FF: calculatorStore.z_Rayl_FF,
      pulseDur: +pulse_duration_ms.value,
      pulseRepetitionInterval: pulseRepetitionInterval.value,
      pulseTrainDuration: pulseTrainDuration.value,
      pulseTrainRampShape: pulse_train_ramp_shape.value,
      pulseTrainRampDuration: pulseTrainRampDuration.value,
      pulseTrainRepetitionRampShape: pulse_train_repetition_ramp_shape.value,
      pulseTrainRepetitionRampDuration: pulseTrainRepetitionRampDuration.value,
      pulseTrainRepetitionInterval: pulseTrainRepetitionInterval.value,
      amplitude: amplitude.value,
      arbitrary_default_amplitude: arbitrary_default_amplitude,
      sampleRate: sampleRate.value,
      nPulses: nPulses.value,
      nTrains: nTrains.value,
      canvas_width: width,
      canvas_height: height,
      time_parameters_valid: time_parameters_valid.value,
      pulse_ramp_shape: pulse_ramp_shape.value,
      pulse_ramp_duration_ms: pulse_ramp_duration_ms.value,
      showPulseTrain: isSecondLevelTimingParameters.value,
      showPulseTrainRepetition: isThirdLevelTimingParameters.value,
      isDarkModeGlobal: isDarkModeGlobal.value,
      ppp_mpa: ppp_mpa.value,
      derated_ppp_mpa: derated_ppp_mpa.value,
      frequency_MHz: frequency_MHz.value,
      pulseTrainRepetitionDuration: pulseTrainRepetitionDuration.value,
      isppaOld: isppa.value,
    }

    const svgState = new WorkerState(data)

    // canvas2svg acts as a drop-in canvas 2D context but records SVG
    const c2sCtx = new C2S(width, height)

    // Polyfill methods not implemented by canvas2svg
    if (typeof c2sCtx.setLineDash !== 'function') {
      c2sCtx.setLineDash = (segments: number[]) => {
        if (segments && segments.length) {
          c2sCtx.__currentElement?.setAttribute('stroke-dasharray', segments.join(','))
        } else {
          c2sCtx.__currentElement?.removeAttribute('stroke-dasharray')
        }
      }
    }
    if (typeof c2sCtx.getLineDash !== 'function') {
      c2sCtx.getLineDash = () => []
    }

    drawTimingGraphs(c2sCtx, svgState)

    const svgString = c2sCtx.getSerializedSvg(true)
    const blob = new Blob([svgString], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link: HTMLAnchorElement = document.createElement('a')
    link.download = filename
    link.href = url
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading canvas as SVG:', error)
  }
}

if (typeof Worker !== "undefined") {
    worker = new Worker({ type: 'module'})
    worker.onmessage = (evt) => {
      loading.value = evt.data.loading
      emit('loading', evt.data.loading)
    }
  // Set up web worker tasks and communication here
} else {
  // Fallback mechanism for browsers without web worker support
  console.error('Your browser does not support web workers')
}

const browser = computed(() => {
  const ua= navigator.userAgent
      let tem
      let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || []

      if (/trident/i.test(M[1])) {
          tem =  /\brv[ :]+(\d+)/g.exec(ua) || []
          return 'IE '+(tem[1] || '')
      }

      if (M[1] === 'Chrome') {
          tem = ua.match(/\b(OPR|Edge)\/(\d+)/)
          if (tem != null) {
            return tem.slice(1).join(' ').replace('OPR', 'Opera')
          }
      }
      return [M[1], M[2]].join(' ')
})

const drawSoundPulseGraph = debounce(() => {
  if (ptrd_isValid.value && typeof Worker !== "undefined" && worker) {
    const message = {
      z_Rayl_IS: calculatorStore.z_Rayl_IS,
      z_Rayl_FF: calculatorStore.z_Rayl_FF,
      pulseDur: +pulse_duration_ms.value,
      pulseRepetitionInterval: pulseRepetitionInterval.value,
      pulseTrainDuration: pulseTrainDuration.value,
      pulseTrainRampShape: pulse_train_ramp_shape.value,
      pulseTrainRampDuration: pulseTrainRampDuration.value,
      pulseTrainRepetitionRampShape: pulse_train_repetition_ramp_shape.value,
      pulseTrainRepetitionRampDuration: pulseTrainRepetitionRampDuration.value,
      pulseTrainRepetitionInterval: pulseTrainRepetitionInterval.value,
      amplitude: amplitude.value,
      arbitrary_default_amplitude: arbitrary_default_amplitude,
      sampleRate: sampleRate.value,
      nPulses: nPulses.value,
      nTrains: nTrains.value,
      canvas_width: canvasWidth.value,
      canvas_height: canvasHeight.value,
      time_parameters_valid: time_parameters_valid.value,
      pulse_ramp_shape: pulse_ramp_shape.value,
      pulse_ramp_duration_ms: pulse_ramp_duration_ms.value,
      showPulseTrain: isSecondLevelTimingParameters.value,
      showPulseTrainRepetition: isThirdLevelTimingParameters.value,
      isDarkModeGlobal: isDarkModeGlobal.value,
      ppp_mpa: ppp_mpa.value,
      derated_ppp_mpa: derated_ppp_mpa.value,
      update: true,
      frequency_MHz: frequency_MHz.value,
      pulseTrainRepetitionDuration: pulseTrainRepetitionDuration.value,
      isppaOld: isppa.value,

    }
    worker.postMessage(message)
  }
}, 500)

function draw() {
    canvasRefreshButton.value = false
    safariLoading.value = false
    drawSoundPulseGraph()
}

function updateAfterResizeParent () {
  console.log(props.parentWidth)
  if (/safari/i.test(browser.value)) {
    canvasRefreshButton.value = true
    safariLoading.value = true
    setTimeout(() => {
      draw()
    },1000)
  } else {
    canvasRefreshButton.value = false
  }
  drawSoundPulseGraph()
}

watch([isSecondLevelTimingParameters, isThirdLevelTimingParameters, isDarkModeGlobal], () => calculatorStore.activateCanvasRefreshButton)

onMounted(() => {
  emit('updateSize')
  if (typeof Worker !== "undefined" && worker) {
    const canvas = canvasRef.value
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1;
    canvas.width =  props.parentWidth.value;

    canvas.height = 800 * dpr;

    const offscreen = canvas.transferControlToOffscreen()

    worker.postMessage({
      canvas: offscreen,
      z_Rayl_IS: calculatorStore.z_Rayl_IS,
      z_Rayl_FF: calculatorStore.z_Rayl_FF,
      pulseDur: +pulse_duration_ms.value,
      pulseRepetitionInterval: pulseRepetitionInterval.value,
      pulseTrainDuration: pulseTrainDuration.value,
      pulseTrainRampDuration: pulseTrainRampDuration.value,
      pulseTrainRepetitionInterval: pulseTrainRepetitionInterval.value,
      amplitude: amplitude.value,
      arbitrary_default_amplitude: arbitrary_default_amplitude,
      sampleRate: sampleRate.value,
      nPulses: nPulses.value,
      nTrains: nTrains.value,
      time_parameters_valid: time_parameters_valid.value,
      pulse_ramp_shape: pulse_ramp_shape.value,
      pulse_ramp_duration_ms: pulse_ramp_duration_ms.value,
      canvas_width: canvasWidth.value,
      canvas_height: canvasHeight.value,
      showPulseTrain: isSecondLevelTimingParameters.value,
      showPulseTrainRepetition: isThirdLevelTimingParameters.value,
      isDarkModeGlobal: isDarkModeGlobal.value,
      ppp_mpa: ppp_mpa.value,
      derated_ppp_mpa: derated_ppp_mpa.value,
      frequency_MHz: frequency_MHz.value,
      pulseTrainRepetitionDuration: pulseTrainRepetitionDuration.value,
      pulseTrainRampShape: pulse_train_ramp_shape.value,
      pulseTrainRepetitionRampShape: pulse_train_repetition_ramp_shape.value,
      pulseTrainRepetitionRampDuration: pulseTrainRepetitionRampDuration.value,
      isppaOld: isppa.value,

    }, [offscreen])
  }
  drawSoundPulseGraph()
})
onBeforeUnmount(() => {
  if (worker && typeof Worker !== "undefined") {
    worker.postMessage({
      cleanup: true,
    })
    worker.terminate()
  }
})
</script>